import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import TippyWrapper from 'components/TippyWrapper';
import BorderFanciness from 'components/Map/AreaMarker/BorderFanciness';
import { SELECTED_LABEL_FILTER_STRING } from 'components/Map/AreaMarker/constants';
import GateIcon from 'components/Map/AreaMarker/GateIcon';
import LockIcon from 'components/Map/AreaMarker/LockIcon';
import { useCursor } from 'components/Map/InteractiveMarker/hooks';
import { MapModalTooltipContextValue } from 'components/Map/MapModalTooltipContext';
import {
  areaToTooltipData,
  isUnterzeeSetting,
} from 'mapping';
import getMinimumZoomLevelForDestinations from 'mapping/getMinimumZoomLevelForDestinations';
import { connect } from 'react-redux';
import { IAppState } from 'types/app';
import {
  IMappableSetting,
  IStateAwareArea,
} from 'types/map';
import { ContainerProps } from './props';

const DRAG_THRESHOLD = 10; // px movement before we treat this as a drag

export function InteractiveMarker({
  area,
  currentArea,
  fallbackMapPreferred,
  onAreaClick,
  onAreaSelect,
  onTapAtLowZoomLevel,
  openModalTooltip,
  selectedArea,
  setting,
  zoomLevel,
}: Props) {
  const MIN_ZOOM_LEVEL_FOR_DESTINATIONS = useMemo(() => {
    if (!setting?.mapRootArea?.areaKey) {
      return 0;
    }
    return getMinimumZoomLevelForDestinations(setting as IMappableSetting);
  }, [setting]);

  const tooltipData = areaToTooltipData(area, currentArea, onAreaClick);

  const [isMouseDragging, setIsMouseDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const tooltipAnchorRef = useRef<HTMLDivElement>(null);

  const isIsland = useMemo(() => isUnterzeeSetting(setting), [setting]);

  const isVisitableIsland: boolean = useMemo(() => {
    if (!isUnterzeeSetting(setting)) {
      return false;
    }
    return area.visitable ?? false;
  }, [
    area.visitable,
    setting,
  ]);

  const onClick = useCallback((e) => {
    // Mouse is no longer down
    setIsMouseDown(false);

    // If we have dragged while the mouse was down, then don't trigger a click either
    if (isMouseDragging) {
      return;
    }

    // This isn't a real click event --- it's a simulated one from Leaflet
    // eslint-disable-next-line no-underscore-dangle
    if (e._simulated) {
      return;
    }

    // If we don't have an onclick callback, then return early
    if (!onAreaClick) {
      return;
    }

    // If we are a landmark, return early
    if (area.isLandmark) {
      return;
    }

    // If we should respond to tap/click at a low zoom level (why would we though?) then do that now
    if (area.shouldZoomOnTapAtZoomLevel(zoomLevel)) {
      if (!isTouchActive) {
        onAreaClick(e, area);
      } else {
        onTapAtLowZoomLevel(area);
      }
      return;
    }

    // If the area shouldn't be interactive at this level of zoom, then also return
    if (!area.shouldBeInteractiveAtZoomLevel(zoomLevel)) {
      return;
    }

    // If this was a mouse click (not a tap event), then actually run the onClick callback!
    if (!isTouchActive) {
      onAreaClick(e, area);
    }
  }, [
    area,
    isMouseDragging,
    isTouchActive,
    onAreaClick,
    onTapAtLowZoomLevel,
    zoomLevel,
  ]);

  const onMouseMove = useCallback((e: MouseEvent) => {
    // Ignore mousemove events when we're not dragging
    if (!isMouseDown) {
      return;
    }

    // Check whether we've moved far enough to be considered "dragging"
    const { clientX, clientY } = e;
    setIsMouseDragging(isMouseDragging
      || Math.abs(clientX - startX) > DRAG_THRESHOLD
      || Math.abs(clientY - startY) > DRAG_THRESHOLD);
  }, [
    isMouseDown,
    isMouseDragging,
    startX,
    startY,
  ]);

  const onMouseDown = useCallback((e) => {
    const { clientX, clientY } = e;
    setIsMouseDown(true);
    setIsMouseDragging(false);
    setStartX(clientX);
    setStartY(clientY);
  }, []);

  const onMouseOut = useCallback(() => {
    onAreaSelect();
    setIsSelected(false);
  }, [
    onAreaSelect,
  ]);

  const onMouseOver = useCallback(() => {
    onAreaSelect(area);
    setIsSelected(true);
  }, [
    area,
    onAreaSelect,
  ]);

  const onTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation(); // Don't send this to the parent
    setIsTouchActive(false);
    if (isTouchDragging) {
      return;
    }

    if (area.shouldZoomOnTapAtZoomLevel(zoomLevel)) {
      onTapAtLowZoomLevel(area);
      return;
    }

    onAreaSelect(area);

    if (area.shouldShowTooltip) {
      openModalTooltip({ ...tooltipData });
    }
  }, [
    area,
    isTouchDragging,
    onAreaSelect,
    onTapAtLowZoomLevel,
    openModalTooltip,
    tooltipData,
    zoomLevel,
  ]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    const { clientX, clientY } = e.touches[0];
    setIsTouchDragging(isTouchDragging
      || Math.abs(clientX - startX) > DRAG_THRESHOLD
      || Math.abs(clientY - startY) > DRAG_THRESHOLD);
  }, [
    isTouchDragging,
    startX,
    startY,
  ]);

  const onTouchStart = useCallback((e: TouchEvent) => {
    const { clientX, clientY } = e.touches[0];
    setIsTouchActive(true);
    setIsTouchDragging(false);
    setStartX(clientX);
    setStartY(clientY);
  }, []);

  const cursor = useCursor(area, zoomLevel);

  // Don't show the decorated border on destinations below min zoom level, unless we're in fallback mode
  const undecorated = area.isDestination && zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS && !fallbackMapPreferred;
  const isSelectedFromProps = selectedArea?.areaKey === area.areaKey;
  const shouldExhibitSelectionGlow = (isSelected || isSelectedFromProps)
    && !area.isLandmark
    && area.shouldBeInteractiveAtZoomLevel(zoomLevel)
    && (!area.isDestination || zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS);

  useEffect(() => {
    const domElement = ref.current;

    if (domElement == null) {
      return () => { /* no-op */ };
    }

    domElement.addEventListener('click', onClick);
    domElement.addEventListener('mousedown', onMouseDown);
    domElement.addEventListener('mousemove', onMouseMove);
    domElement.addEventListener('mouseout', onMouseOut);
    domElement.addEventListener('mouseover', onMouseOver);
    domElement.addEventListener('touchend', onTouchEnd);
    domElement.addEventListener('touchmove', onTouchMove);
    domElement.addEventListener('touchstart', onTouchStart);

    return () => {
      domElement.removeEventListener('click', onClick);
      domElement.removeEventListener('mousedown', onMouseDown);
      domElement.removeEventListener('mousemove', onMouseMove);
      domElement.removeEventListener('mouseout', onMouseOut);
      domElement.removeEventListener('mouseover', onMouseOver);
      domElement.removeEventListener('touchend', onTouchEnd);
      domElement.addEventListener('touchmove', onTouchMove);
      domElement.removeEventListener('touchstart', onTouchStart);
    };
  }, [
    onClick,
    onMouseDown,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
  ]);

  const tippyChildComponent = useMemo(() => (
    <div
      ref={tooltipAnchorRef}
      className={classnames(
        'leaflet-tooltip--fbg__name',
        undecorated && 'leaflet-tooltip--fbg__name--no-decoration',
        area.isDistrict && 'leaflet-tooltip--fbg__name--district',
        area.isDestination && 'leaflet-tooltip--fbg__name--destination',
        area.isLandmark && 'leaflet-tooltip--fbg__name--landmark',
        isIsland && 'leaflet-tooltip--fbg__name--island',
        isVisitableIsland && 'leaflet-tooltip--fbg__name--visitable-island',

        (area.isDistrict && !area.shouldBeInteractiveAtZoomLevel(zoomLevel))
        && 'leaflet-tooltip--fbg__name--district--non-interactive',
      )}
      style={{
        boxShadow: (shouldExhibitSelectionGlow) ? '0 0 4px white' : 'none',
        filter: (shouldExhibitSelectionGlow) ? SELECTED_LABEL_FILTER_STRING : undefined,
        transition: 'boxShadow .2s, filter .2s',
      }}
    >
      <div style={{ cursor }}>
        {area.name}
      </div>
    </div>
  ), [
    area,
    cursor,
    isIsland,
    isVisitableIsland,
    shouldExhibitSelectionGlow,
    undecorated,
    zoomLevel,
  ]);

  return (
    <>
      <div
        ref={ref}
        style={{
          cursor,
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          imageRendering: 'pixelated',
          padding: '16px',
        }}
      >
        {area.shouldBeInteractiveAtZoomLevel(zoomLevel) && area.shouldShowGateIcon && (
          <GateIcon
            area={area}
            selected={shouldExhibitSelectionGlow}
          />
        )}
        {area.shouldBeInteractiveAtZoomLevel(zoomLevel) && area.shouldShowLockIcon && (
          <LockIcon
            area={area}
            selected={shouldExhibitSelectionGlow}
          />
        )}
        {area.shouldShowBorderFancinessAtZoomLevel(zoomLevel) && (
          <BorderFanciness
            side="top"
            visible={area.shouldBeInteractiveAtZoomLevel(zoomLevel)}
            selected={shouldExhibitSelectionGlow}
          />
        )}

        {tooltipData.description ? (
          <TippyWrapper tooltipData={tooltipData}>
            {tippyChildComponent}
          </TippyWrapper>
        ) : (tippyChildComponent)}

        {area.shouldShowBorderFancinessAtZoomLevel(zoomLevel) && (
          <BorderFanciness
            side="bottom"
            visible={area.shouldBeInteractiveAtZoomLevel(zoomLevel)}
            selected={isSelected}
          />
        )}
      </div>
    </>
  );
}

const mapStateToProps = ({ map: { fallbackMapPreferred, setting } }: IAppState) => ({
  fallbackMapPreferred,
  setting,
});

type Props = ContainerProps
  & Pick<MapModalTooltipContextValue, 'openModalTooltip'>
  & ReturnType<typeof mapStateToProps>
  & { selectedArea?: IStateAwareArea };

export default connect(mapStateToProps)(InteractiveMarker);
